import React, {useEffect, useState} from "react";
import {ProjectColors} from "../../../Enums/Core/projectColors";
import {Col, Form, Row, Spinner} from "react-bootstrap";
import DataMultiSelector from "../DataMultiSelector/DataMultiSelector";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import StackedBarChart from "../../StackedBarChart/StackedBarChart";
import {
    BAR_CHAR_ENTITIES_LEN_LIMIT, HIGH_FLOW_LEAK, LOW_FLOW_LEAK, MAX_SELECTION_LIMIT, MODERATE_FLOW_LEAK
} from "../../../Constants/constants";
import Sort from "../SortComponent/Sort";
import {
    QUERY_GET_LEAK_VOLUME_AND_CONSUMPTION
} from "../../../GraphQL/ReportGenerator/queries";
import {apolloClientReports} from "../../../GraphQL/config";
import {
    filterPreviousSelectedEntities, getLastMonthEndDate, getLastMonthStartDate, getResponseTypeList, gqlDateFormat
} from "../Utils/utils";
import moment from "moment/moment";
import convert, {DEFAULT_VOLUME_UNIT} from "../MeasurementText/MeasurementTextConverter";

export default function LeakVolumeAndWaterConsumption(props) {
    const sortOptions = [
        {value: 'name', label: `${props.entityType} Name`},
        {value: 'leakVolume', label: 'Leak Volume'},
        {value: 'consumption', label: 'Water Consumption'},
        {value: 'ratio', label: 'Leak/Consumption Ratio'},
        {value: 'high', label: 'High Flow Leak Volume'},
        {value: 'moderate', label: 'Moderate Flow Leak Volume'},
        {value: 'low', label: 'Low Flow Leak Volume'}
    ]

    const BAR_CHART_HEIGHT = 270;
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSort, setSelectedSort] = useState(sortOptions[1].value);
    const [startDate, setStartDate] = useState(getLastMonthStartDate());
    const [endDate, setEndDate] = useState(getLastMonthEndDate());
    const [entities, setEntities] = useState();
    const [selectedEntities, setSelectedEntities] = useState();
    const [dateApplied, setDateApplied] = useState(false);
    const [filteredEntities, setFilteredEntities] = useState();
    const [previousSelectedValues, setPreviousSelectedValues] = useState(null)
    const [chartData, setChartData] = useState();
    const [csvData, setCsvData] = useState([]);
    const [chartMinHeight, setChartMinHeight] = useState(200);
    const [chartPointWidth, setChartPointWidth] = useState(15);
    const [volumeUnit, setVolumeUnit] = useState(DEFAULT_VOLUME_UNIT);
    const [includeTypes, setIncludeTypes] = useState({
        'MAIN_INLET': false, 'COOLING_TOWER': false, 'OTHER': true
    });
    const entityType = props.entityLabel;
    const [systemTypeGroups, setSystemTypeGroups] = useState(['OTHER'])

    const calculateChartParams = (selectedEntities) => {
        if (!selectedEntities) {
            return
        }
        if (selectedEntities.length <= BAR_CHAR_ENTITIES_LEN_LIMIT) {
            setChartPointWidth(15)
            setChartMinHeight(60 * selectedEntities.length)
        } else {
            setChartPointWidth(10)
            setChartMinHeight(20 * selectedEntities.length)
        }
    }

    const getLeakVolumeAndWaterConsumptionDataFromDB = () => {
        apolloClientReports.query({
            query: QUERY_GET_LEAK_VOLUME_AND_CONSUMPTION, variables: {
                from: gqlDateFormat(startDate),
                to: gqlDateFormat(endDate),
                entityType: entityType,
                systemTypeGroups: systemTypeGroups
            }
        }).then(res => {
            let response = res?.data?.leakVolumeAndWaterConsumptionEntityChart;
            if (!response) {
                console.error(`Data from getWaterConsumptionAndPotentialWaste is empty`);
            } else {
                setVolumeUnit(convert(response.measureUnit.volumeUnit));
                const types = response.entities.map(o => o.type[0])
                const typeArr = getResponseTypeList(types)
                const ids = response?.entities?.map(o => o.id);
                const unique = response?.entities?.filter(({id}, index) => !ids.includes(id, index + 1))
                let sortedEntities = unique.slice().sort((a, b) => parseFloat(a.data.severity[0].value + a.data.severity[1].value + a.data.severity[2].value) - parseFloat(b.data.severity[0].value + b.data.severity[1].value + b.data.severity[2].value)).reverse()
                setEntities(sortedEntities.map((entity) => entity));
                if (!selectedEntities || dateApplied) {
                    setDateApplied(false);
                    let currentValuesArr = sortedEntities.map((entity) => entity).slice(0, MAX_SELECTION_LIMIT)
                    if (previousSelectedValues) {
                        let PreviousValuesArr = filterPreviousSelectedEntities(typeArr, currentValuesArr, previousSelectedValues)
                        PreviousValuesArr.forEach((value) => {
                            currentValuesArr.push(value)
                        })
                    }
                    setSelectedEntities(currentValuesArr);
                }
                setCsvData(buildCsvData(sortedEntities));
                setIsLoading(false);
            }
        })
    }

    const buildCsvData = (responseData) => {
        let csvData = [];
        let headers = ['From', 'To', entityType, 'Leak volume', 'Water consumption', 'Ratio', 'Low flow leak volume', 'Moderate flow leak volume', 'High flow leak volume'];

        csvData.push(headers);

        responseData.forEach((entity, index) => {
            let row = [];
            if (index === 0) {
                row.push(moment(startDate).format('LL'), moment(endDate).format('LL'));
            } else {
                row.push('', '');
            }
            let leakCounts = entity.data.severity.map((severityElement) => severityElement.value);
            let total = leakCounts.reduce((a, b) => a + b, 0);
            row.push(entity.name, total, entity.data.waterConsumption, total / entity.data.waterConsumption * 100, leakCounts[0], leakCounts[1], leakCounts[2]);
            csvData.push(row);
        });

        return csvData;
    }
    useEffect(() => {
        if (selectedEntities) {
            setCsvData(buildCsvData(selectedEntities));
        }
        calculateChartParams(selectedEntities)
    }, [selectedEntities, chartMinHeight, chartPointWidth])

    useEffect(() => {
        setIsLoading(true);
        getLeakVolumeAndWaterConsumptionDataFromDB();
    }, [dateApplied]);

    useEffect(() => {
        setIsLoading(true);
        getLeakVolumeAndWaterConsumptionDataFromDB();
    }, [systemTypeGroups]);

    useEffect(() => {
        switch (selectedSort) {
            case 'name':
                setFilteredEntities(filteredEntities?.sort((a, b) => a.name.localeCompare(b.name)));
                break;
            case 'ratio':
                // TODO: sort by the ratio field (not implemented on backend yet)
                setFilteredEntities(filteredEntities?.sort((a, b) => parseFloat(a.data.waterConsumption) - parseFloat(b.data.waterConsumption)).reverse());
                break;
            case 'high':
                setFilteredEntities(filteredEntities?.sort((a, b) => parseFloat(a.data.severity[2].value) - parseFloat(b.data.severity[2].value)).reverse());
                break;
            case 'moderate':
                setFilteredEntities(filteredEntities?.sort((a, b) => parseFloat(a.data.severity[1].value) - parseFloat(b.data.severity[1].value)).reverse());
                break;
            case 'low':
                setFilteredEntities(filteredEntities?.sort((a, b) => parseFloat(a.data.severity[0].value) - parseFloat(b.data.severity[0].value)).reverse());
                break;
            case 'consumption':
                setFilteredEntities(filteredEntities?.sort((a, b) => parseFloat(a.data.waterConsumption) - parseFloat(b.data.waterConsumption)).reverse());
                break;
            default:
                // leakVolume
                setFilteredEntities(filteredEntities?.sort((a, b) => parseFloat(a.data.severity[0].value + a.data.severity[1].value + a.data.severity[2].value) - parseFloat(b.data.severity[0].value + b.data.severity[1].value + b.data.severity[2].value)).reverse());
                break;
        }
    }, [selectedSort, filteredEntities]);

    useEffect(() => {
        let keys = Object.keys(includeTypes);
        let filters = keys.filter((key) => includeTypes[key]);
        setFilteredEntities(selectedEntities)
        setSystemTypeGroups(filters)
    }, [includeTypes, selectedEntities]);

    useEffect(() => {
        setChartData([{
            name: 'Water consumption',
            data: filteredEntities?.map((entity) => entity.data.waterConsumption),
            stack: 'totalFlow',
            color: '#229af6',
            showInLegend: false
        }, {
            name: 'Low Flow Leak Volume', data: filteredEntities?.map((entity) => entity.data.severity)
                .map((b) => b.filter((a) => a.label === LOW_FLOW_LEAK))
                .map((entity) => entity[0].value), stack: 'flowValue', color: ProjectColors.Orange, showInLegend: true
        }, {
            name: 'Moderate Flow Leak Volume', data: filteredEntities?.map((entity) => entity.data.severity)
                .map((b) => b.filter((a) => a.label === MODERATE_FLOW_LEAK))
                .map((entity) => entity[0].value), stack: 'flowValue', color: ProjectColors.Red, showInLegend: true
        }, {
            name: 'High Flow Leak Volume', data: filteredEntities?.map((entity) => entity.data.severity)
                .map((b) => b.filter((a) => a.label === HIGH_FLOW_LEAK))
                .map((entity) => entity[0].value), stack: 'flowValue', color: ProjectColors.DarkRed, showInLegend: true
        },]);
    }, [filteredEntities, selectedSort]);

    return (<>
        {(!entities || !selectedEntities || !chartData) && <Spinner animation="border" variant="primary"/>}
        {entities && selectedEntities && chartData && <div>
            <Row>
                <Col md={12}>
                    <p className={'card-title-main bold-title'}>{props.title}</p>
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <DataMultiSelector
                        variant={props.variant}
                        setSelectedValues={setSelectedEntities}
                        entities={entities?.map((entity) => {
                            return {...entity, value: entity.value, label: entity.name}
                        })}
                        selectedValues={selectedEntities?.map((entity) => {
                            return {...entity, value: entity.value, label: entity.name}
                        })}/>
                </Col>
                <Col md={8} className={'DatePicker'}>
                    <DateRangePicker startDate={startDate} endDate={endDate} setStartDate={setStartDate}
                                     setEndDate={setEndDate} hasExportCsv
                                     setDateApplied={setDateApplied}
                                     csvData={csvData}
                                     title={`Leak volume`}/>
                </Col>
            </Row>
            <br/>
            {isLoading && <Spinner animation="border" variant="primary"/>}
            {!isLoading && <>
                <Sort sortOptions={sortOptions} value={selectedSort} setSelectedSort={setSelectedSort}/>
                <Row>
                    <Col>
                        <StackedBarChart
                            volumeUnit={volumeUnit}
                            scrollbar
                            minScrollHeight={chartMinHeight}
                            chartPointWidth={chartPointWidth}
                            height={BAR_CHART_HEIGHT}
                            horizontal
                            totalName={'Total Leaks Volume'}
                            categories={filteredEntities?.map(e => {
                                return e.value !== null ? e.name : null
                            }).filter((a) => a)}
                            data={chartData}/>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Row className={'FontSize'}>
                            <Col md={3} className={'PullLeftInclude'}>
                                <p>SHOW:</p>
                            </Col>
                            <Col md={9} className={'PullLeftIncludeButtons'}>
                                <Form>
                                    <Row>
                                        <Col md={4}>
                                            <Form.Check
                                                disabled={!includeTypes.COOLING_TOWER && !includeTypes.OTHER && includeTypes.MAIN_INLET}
                                                type='checkbox'
                                                label='Main Inlet'
                                                onChange={() => {
                                                    setIncludeTypes({
                                                        ...includeTypes, MAIN_INLET: !includeTypes.MAIN_INLET
                                                    })
                                                    setPreviousSelectedValues(selectedEntities)
                                                    setSelectedEntities(null)
                                                }}
                                                checked={includeTypes.MAIN_INLET}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Form.Check
                                                disabled={includeTypes.COOLING_TOWER && !includeTypes.OTHER && !includeTypes.MAIN_INLET}
                                                type='checkbox'
                                                label='Cooling Tower'
                                                onChange={() => {
                                                    setIncludeTypes({
                                                        ...includeTypes, COOLING_TOWER: !includeTypes.COOLING_TOWER
                                                    })
                                                    setPreviousSelectedValues(selectedEntities)
                                                    setSelectedEntities(null)
                                                }}
                                                checked={includeTypes.COOLING_TOWER}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <Form.Check
                                                disabled={!includeTypes.COOLING_TOWER && includeTypes.OTHER && !includeTypes.MAIN_INLET}
                                                type='checkbox'
                                                label='All The Rest'
                                                onChange={() => {
                                                    setIncludeTypes({
                                                        ...includeTypes, OTHER: !includeTypes.OTHER
                                                    })
                                                    setPreviousSelectedValues(selectedEntities)
                                                    setSelectedEntities(null)
                                                }}
                                                checked={includeTypes.OTHER}
                                            />
                                        </Col>
                                    </Row>
                                </Form>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </>}
        </div>}
    </>)
}
